import { memo } from 'react';
import { KanbanSquare, CheckCircle2, Users, Eye } from 'lucide-react';

import { cn } from '../../utils/cn';

export interface IncidentSummaryBadgesData {
  kanban_links_count: number;
  kanban_open_cards_count: number;
  kanban_done_cards_count: number;
  assignees_count: number;
  watchers_count: number;
}

export interface IncidentSummaryBadgesProps {
  summary: IncidentSummaryBadgesData;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Compact badge row for `IncidentWithKanbanSummary` augmented incidents on list views.
 * Hides badges whose count is zero so cards stay tidy.
 */
export const IncidentSummaryBadges = memo(
  ({ summary, size = 'sm', className }: IncidentSummaryBadgesProps) => {
    const showLinks = summary.kanban_links_count > 0;
    const showDone = summary.kanban_done_cards_count > 0;
    const showAssignees = summary.assignees_count > 0;
    const showWatchers = summary.watchers_count > 0;

    if (!showLinks && !showDone && !showAssignees && !showWatchers) return null;

    const baseClasses = cn('badge', size === 'md' && 'text-xs px-2.5');
    const iconClass = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
        {showLinks && (
          <span
            className={cn(
              baseClasses,
              'bg-accent-500/15 border-accent-500/25 text-accent-200',
            )}
            title={`${summary.kanban_links_count} bridged kanban card${
              summary.kanban_links_count === 1 ? '' : 's'
            } (${summary.kanban_open_cards_count} open)`}
          >
            <KanbanSquare className={iconClass} />
            {summary.kanban_links_count}
            {summary.kanban_open_cards_count > 0 && (
              <span className="opacity-70">/ {summary.kanban_open_cards_count} open</span>
            )}
          </span>
        )}
        {showDone && (
          <span
            className={cn(
              baseClasses,
              'bg-green-500/15 border-green-500/25 text-green-300',
            )}
            title={`${summary.kanban_done_cards_count} bridged card${
              summary.kanban_done_cards_count === 1 ? '' : 's'
            } in a "done" list`}
          >
            <CheckCircle2 className={iconClass} />
            {summary.kanban_done_cards_count} done
          </span>
        )}
        {showAssignees && (
          <span
            className={cn(
              baseClasses,
              'bg-blue-500/15 border-blue-500/25 text-blue-300',
            )}
            title={`${summary.assignees_count} staff assigned`}
          >
            <Users className={iconClass} />
            {summary.assignees_count}
          </span>
        )}
        {showWatchers && (
          <span
            className={cn(
              baseClasses,
              'bg-purple-500/15 border-purple-500/25 text-purple-300',
            )}
            title={`${summary.watchers_count} watcher${
              summary.watchers_count === 1 ? '' : 's'
            } receiving hourly digests`}
          >
            <Eye className={iconClass} />
            {summary.watchers_count}
          </span>
        )}
      </div>
    );
  },
);
IncidentSummaryBadges.displayName = 'IncidentSummaryBadges';
